import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  TrophyIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  ArrowRightIcon,
  FireIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline'
import PerExerciseView from './PerExerciseView'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { weeklyProgramsAPI } from '../../services/api'
import { useChartTheme } from '../../hooks/useChartTheme'

function ScoreBadge({ score }) {
  if (score == null) return null
  const rounded = Math.round(score)
  let bg = 'bg-gray-100 text-gray-700'
  if (rounded >= 80) bg = 'bg-green-100 text-green-700'
  else if (rounded >= 60) bg = 'bg-blue-100 text-blue-700'
  else if (rounded >= 40) bg = 'bg-amber-100 text-amber-700'
  else bg = 'bg-red-100 text-red-700'

  return (
    <span className={`text-sm font-bold px-2 py-1 rounded-full ${bg}`}>
      {rounded}
    </span>
  )
}

function ScoreTrendsMini() {
  // VH-133: recharts takes colours as props, so it inherits nothing from
  // the design system — these follow the active theme instead.
  const chart = useChartTheme()
  const { t } = useTranslation()

  const { data: trendsData } = useQuery({
    queryKey: ['scoreTrends', 12],
    queryFn: () => weeklyProgramsAPI.getScoreTrends(12),
    select: (res) => res.data,
  })

  const trends = trendsData?.trends || trendsData || []
  if (!Array.isArray(trends) || trends.length < 2) return null

  const chartData = [...trends].reverse().map((t) => ({
    week: t.week_dates || `W${t.week}`,
    score: Math.round(t.total_score || 0),
  }))

  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="flex items-center gap-2 mb-3">
          <ArrowTrendingUpIcon className="h-5 w-5 text-indigo-600" />
          <h4 className="font-medium text-gray-900">{t('exerciseProgress.scoreTrend', 'Score Trend')}</h4>
        </div>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: chart.axis }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: chart.axis }} />
              <Tooltip {...chart.tooltipProps} />
              <Line
                type="linear"
                dataKey="score"
                stroke={chart.primary}
                strokeWidth={2}
                dot={{ r: 3, fill: '#4F46E5' }}
                name={t('exerciseProgress.score', 'Score')}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

function ProgramWeekCard({ program }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const { data: scoreData } = useQuery({
    queryKey: ['programScore', program.id],
    queryFn: () => weeklyProgramsAPI.getScore(program.id),
    select: (res) => res.data,
    enabled: !!program.id,
  })

  const { data: breakdownData } = useQuery({
    queryKey: ['scoreBreakdown', program.id],
    queryFn: () => weeklyProgramsAPI.getScoreBreakdown(program.id),
    select: (res) => res.data,
    enabled: expanded && !!program.id,
  })

  const score = scoreData?.total_score
  const weekDates = scoreData?.week_dates || program.week_dates
  const factors = scoreData?.factors || {}
  const sessions = breakdownData?.session_summaries || breakdownData?.sessions || []

  const weekLabel = weekDates || (() => {
    if (program.year && program.week) return `${t('exerciseProgress.week', 'Week')} ${program.week}, ${program.year}`
    if (program.submitted_at) {
      const d = new Date(program.submitted_at)
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    }
    return `#${program.id}`
  })()

  return (
    <div className="card overflow-hidden">
      <div
        className="card-body cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Summary row */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900">{weekLabel}</p>
            {scoreData && (
              <div className="flex gap-3 mt-1 text-xs text-gray-500">
                {scoreData.sessions_completed != null && (
                  <span>{scoreData.sessions_completed}/{scoreData.sessions_planned} {t('exerciseProgress.sessions', 'sessions')}</span>
                )}
                {scoreData.new_pbs > 0 && (
                  <span className="flex items-center text-amber-600">
                    <TrophyIcon className="h-3 w-3 mr-0.5" />
                    {scoreData.new_pbs} {t('exerciseProgress.newPBs', 'new PBs')}
                  </span>
                )}
                {scoreData.is_personal_best && (
                  <span className="flex items-center text-amber-500">
                    <StarIcon className="h-3 w-3 mr-0.5" />
                    {t('exerciseProgress.bestWeek', 'Best week!')}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <ScoreBadge score={score} />
            {expanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
          </div>
        </div>

        {/* Expanded content */}
        {expanded && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            {/* Score factor breakdown */}
            {Object.keys(factors).length > 0 && (
              <>
                <p className="text-xs font-medium text-gray-500 mb-2">{t('exerciseProgress.scoreBreakdown', 'Score Breakdown')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {[
                    { key: 'volume_factor', label: t('exerciseProgress.volume', 'Volume'), color: 'text-blue-600', bg: 'bg-blue-50' },
                    { key: 'intensity_factor', label: t('exerciseProgress.intensity', 'Intensity'), color: 'text-orange-600', bg: 'bg-orange-50' },
                    { key: 'progression_factor', label: t('exerciseProgress.progression', 'Progression'), color: 'text-green-600', bg: 'bg-green-50' },
                    { key: 'consistency_factor', label: t('exerciseProgress.consistency', 'Consistency'), color: 'text-purple-600', bg: 'bg-purple-50' },
                  ].map(({ key, label, color, bg }) => {
                    const val = factors[key]
                    if (val == null) return null
                    return (
                      <div key={key} className={`${bg} rounded-lg p-2 text-center`}>
                        <p className={`text-lg font-bold ${color}`}>{Math.round(val)}</p>
                        <p className="text-xs text-gray-500">{label}</p>
                      </div>
                    )
                  })}
                </div>
              </>
            )}

            {/* Metrics */}
            {scoreData && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {scoreData.total_volume_kg != null && (
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">{Math.round(scoreData.total_volume_kg).toLocaleString()} kg</p>
                    <p className="text-xs text-gray-400">{t('exerciseProgress.totalVolume', 'Total Volume')}</p>
                  </div>
                )}
                {scoreData.avg_rpe != null && (
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">{Number(scoreData.avg_rpe).toFixed(1)}</p>
                    <p className="text-xs text-gray-400">{t('exerciseProgress.avgRPE', 'Avg RPE')}</p>
                  </div>
                )}
                {scoreData.volume_vs_prev_week_percent != null && (
                  <div className="text-center">
                    <p className={`text-sm font-semibold ${Number(scoreData.volume_vs_prev_week_percent) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Number(scoreData.volume_vs_prev_week_percent) >= 0 ? '+' : ''}{Number(scoreData.volume_vs_prev_week_percent).toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-400">{t('exerciseProgress.vsLastWeek', 'vs last week')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Session details with exercises */}
            {sessions.length > 0 && (
              <>
                <p className="text-xs font-medium text-gray-500 mb-2">{t('exerciseProgress.sessionDetails', 'Session Details')}</p>
                <div className="space-y-3">
                  {sessions.map((session, sIdx) => (
                    <div key={sIdx} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm font-medium text-gray-900">
                          {session.session_name || session.name || `Session ${sIdx + 1}`}
                        </p>
                        {session.composite_score != null && (
                          <ScoreBadge score={session.composite_score} />
                        )}
                      </div>
                      <div className="flex gap-3 text-xs text-gray-500 mb-2">
                        {session.exercises_completed != null && (
                          <span>{session.exercises_completed}/{session.exercises_total || '?'} {t('exerciseProgress.exercisesDone', 'exercises')}</span>
                        )}
                        {session.new_pbs > 0 && (
                          <span className="text-amber-600">
                            <TrophyIcon className="h-3 w-3 inline mr-0.5" />
                            {session.new_pbs} PB
                          </span>
                        )}
                        {session.total_volume_kg != null && (
                          <span>{Math.round(session.total_volume_kg).toLocaleString()} kg</span>
                        )}
                      </div>

                      {/* Exercise list within session */}
                      {session.exercises?.length > 0 && (
                        <div className="space-y-1 mt-2">
                          {session.exercises.map((ex, eIdx) => {
                            // VH-186: exclude prefilled rows. They carry the best
                            // PREVIOUS session's numbers as a target to beat, so
                            // reducing over them presents last time's lift as
                            // this session's result — on a session that may never
                            // have been performed. Same rule as the reported
                            // badge: a prefill is not a performance.
                            const bestSet = ex.set_results
                              ?.filter((s) => s.set_type === 'working' && !s.is_prefill)
                              ?.reduce((best, s) => {
                                if (!best) return s
                                return (s.actual_weight || 0) > (best.actual_weight || 0) ? s : best
                              }, null)

                            return (
                              <div key={eIdx} className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-700">
                                    {ex.snapshot_exercise_name_en || ex.exercise_name || ex.name}
                                  </span>
                                  {ex.is_skipped && (
                                    <span className="text-xs text-red-400">{t('exerciseProgress.skipped', 'skipped')}</span>
                                  )}
                                  {ex.set_results?.some((s) => s.is_new_pb) && (
                                    <TrophyIcon className="h-3 w-3 text-amber-500" />
                                  )}
                                </div>
                                <div className="flex gap-2 text-xs text-gray-500">
                                  {bestSet && (
                                    <span className="font-medium text-gray-700">
                                      {bestSet.actual_weight != null ? `${bestSet.actual_weight}kg` : ''}
                                      {bestSet.actual_weight != null && bestSet.actual_reps != null ? ' × ' : ''}
                                      {bestSet.actual_reps != null ? `${bestSet.actual_reps}` : ''}
                                    </span>
                                  )}
                                  {ex.personal_best?.max_weight != null && (
                                    <span className="text-amber-600">
                                      PB: {ex.personal_best.max_weight}kg
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default function WeeklyExerciseProgress() {
  const { t } = useTranslation()
  // VH-64: Sub-view toggle
  const [subView, setSubView] = useState('weekly')

  const { data: programsData, isLoading } = useQuery({
    queryKey: ['submittedPrograms'],
    queryFn: () => weeklyProgramsAPI.getPrograms({ status: 'submitted', limit: 12, order_by: '-submitted_at' }),
    select: (res) => res.data,
  })

  // VH-64: Sub-view tabs
  const renderSubViewTabs = () => (
    <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4">
      <button
        onClick={() => setSubView('weekly')}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          subView === 'weekly' ? 'bg-surface-container text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <FireIcon className="h-4 w-4" />
        {t('exerciseProgress.weeklyOverview', 'Weekly Overview')}
      </button>
      <button
        onClick={() => setSubView('perExercise')}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          subView === 'perExercise' ? 'bg-surface-container text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <ListBulletIcon className="h-4 w-4" />
        {t('exerciseProgress.perExercise', 'Per Exercise')}
      </button>
    </div>
  )

  // VH-64: Per Exercise sub-view
  if (subView === 'perExercise') {
    return (
      <div className="space-y-4">
        {renderSubViewTabs()}
        <PerExerciseView />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        {renderSubViewTabs()}
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  const programs = programsData?.programs || programsData || []

  if (!Array.isArray(programs) || programs.length === 0) {
    return (
      <div>
        {renderSubViewTabs()}
        <div className="card">
          <div className="card-body text-center py-8">
            <FireIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">{t('exerciseProgress.noPrograms', 'No submitted programs yet')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('exerciseProgress.noProgramsHint', 'Submit a weekly training program to see your progress')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {renderSubViewTabs()}
      <ScoreTrendsMini />

      <h4 className="font-medium text-gray-900">{t('exerciseProgress.weeklyPrograms', 'Weekly Programs')}</h4>
      {programs.map((program) => (
        <ProgramWeekCard key={program.id} program={program} />
      ))}

      {/* Link to full training page */}
      <div className="text-center">
        <a
          href="/training"
          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800"
        >
          {t('exerciseProgress.goToTraining', 'Go to full Training page')}
          <ArrowRightIcon className="h-4 w-4 ml-1" />
        </a>
      </div>
    </div>
  )
}
